import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header'
import { HeaderContainer, LogoContainer, SidebarProfileSection, SidebarSearchForm } from './styles'
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import ProfileImage from '../common/ProfileImage';
import Logo from '../common/Logo';
import { UnstyledButton } from '../common/Button';
import VipIcon from '../Icons/VipIcons';
import { SEARCH_PAGE_URL } from '../../constants/systemConstants';
import { toast } from '../../utils/notify';

const HeaderLayout = () => {
  const { currentUser, logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isDesktopMoreOpen, setIsDesktopMoreOpen] = useState(false);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    navigate(q ? `${SEARCH_PAGE_URL}?q=${encodeURIComponent(q)}` : SEARCH_PAGE_URL);
  };

  const handleSearchIconClick = () => {
    if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const isSidebarExpanded = isDesktopMoreOpen || isSidebarHovered;

  useEffect(() => {
    if (!isSidebarExpanded && searchInputRef.current && document.activeElement === searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, [isSidebarExpanded]);

  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent double-clicks
    
    try {
      setIsLoggingOut(true);
      await logout();
      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Failed to logout. Please try again.');
    } finally {
      // Always reset the loading state
      setIsLoggingOut(false);
    }
  };
  
  // Don't render anything if no user
  if (!currentUser) {
    return null;
  }
  
  // Show loading spinner while logging out
  if (isLoggingOut) {
    return (
      <HeaderContainer>
        <LoadingSpinner size={24} text="Logging out..." />
      </HeaderContainer>
    );
  }
  
  return (
    <HeaderContainer
      data-more-open={isDesktopMoreOpen ? 'true' : undefined}
      data-expanded={isSidebarExpanded ? 'true' : undefined}
      data-sidebar-expanded={isSidebarExpanded ? 'true' : undefined}
      onMouseEnter={() => setIsSidebarHovered(true)}
      onMouseLeave={() => setIsSidebarHovered(false)}
    >
      <LogoContainer onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
        <Logo variant="both" />
      </LogoContainer>
      <SidebarSearchForm onSubmit={handleSearchSubmit} role="search" data-has-value={searchQuery.length > 0 ? 'true' : undefined}>
        <span className="search-icon" onClick={handleSearchIconClick} aria-hidden="true">
          <VipIcon name="Search" size={18} />
        </span>
        <input
          ref={searchInputRef}
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search"
          aria-label="Search"
        />
        {searchQuery.length > 0 && (
          <UnstyledButton
            type="button"
            className="search-clear"
            aria-label="Clear search"
            onClick={() => {
              setSearchQuery('');
              searchInputRef.current?.focus();
            }}
          >
            <VipIcon name="CloseXCircle" size={16} />
          </UnstyledButton>
        )}
      </SidebarSearchForm>
      <Header 
        isLoggedIn={true}
        onLogout={handleLogout}
        onDesktopMoreToggle={setIsDesktopMoreOpen}
      />
      <SidebarProfileSection>
        <div onClick={() => navigate(`/${currentUser?.username}`)} style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0, overflow: 'hidden', cursor: 'pointer' }}>
          <ProfileImage
            user={currentUser}
            size="40px"
            isVerified={currentUser?.isVerified}
            style={{ flexShrink: 0, width: '40px', height: '40px', minWidth: '40px' }}
          />
          <div className="user-info">
            <div className="fullname" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {currentUser?.fullName || currentUser?.username || 'User'}
              </span>
              {currentUser?.verified && (
                <VipIcon name="VerifiedBadge" size={14} style={{ flexShrink: 0 }} />
              )}
            </div>
            <div className="username">@{currentUser?.username}</div>
          </div>
        </div>
        <UnstyledButton className="logout-button" onClick={(e) => { e.stopPropagation(); handleLogout(); }} title="Logout">
          <VipIcon name="Logout" size={20} />
        </UnstyledButton>
      </SidebarProfileSection>
    </HeaderContainer>
  );
};

export default HeaderLayout;